import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from './../../../environments/environment';

import { User } from 'src/app/models/User';
import { Token, TwoFactorChallenge, TwoFactorSetup, TwoFactorStatus, isTwoFactorChallenge } from 'src/app/models/Token';
import { ResendTokenResponse } from 'src/app/models/ResendTokenResponse';
import { AccountMemberSession } from 'src/app/models/AccountMember';
import { keepNewBadgeMarks } from '../utils/new-badge';

const USER_KEY = 'SMSFUNNEL::AUTH';
const GLOBAL_ACCOUNT = 'SMSFUNNEL::GLOBAL_ACCOUNT';
const SIMULATED_USER_KEY = 'SMSFUNNEL::SIMULATED_USER';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  get isSimulating() {
    const user = this.getLoggedInUser();
    const simulatedUser = this.getSimulatedUser();
    return simulatedUser && simulatedUser.id != user.id;
  }

  /**
   * Realm da conta em uso (CU-86akktdka).
   *
   * A fonte da verdade é o `globalActivated` do usuário EFETIVO — exatamente o
   * campo de onde o `UserInterceptor` tira o `&global=` de cada requisição, e
   * que `getUser()` devolve já submetido ao invariante de acesso (internacional
   * exige `intl_enabled`, ver `enforceRealmAccess`).
   *
   * Antes eram duas cópias do mesmo fato, com donos diferentes: a chave
   * `SMSFUNNEL::GLOBAL_ACCOUNT`, sempre global, e o `globalActivated`, que
   * durante a simulação vive dentro do usuário simulado. Bastava entrar ou sair
   * de uma simulação para elas discordarem — e discordar significava tema, menu
   * e aba da carteira dizendo "internacional" enquanto toda requisição pedia
   * dado nacional, sem nenhum sinal na tela. Com carteira em duas moedas isso é
   * erro de leitura de dinheiro.
   *
   * Derivar do usuário efetivo não conserta a divergência: tira a possibilidade
   * dela existir, porque passa a haver um valor só. Não reintroduza a leitura da
   * chave aqui.
   *
   * A chave sobrevive com o único papel que restou: lembrar o último realm
   * conhecido do DISPOSITIVO para as telas sem sessão (login, cadastro,
   * recuperação de senha — ver `AccountVersionThemeDirective`). Sem sessão não
   * sai requisição com `&global=`, então ali não há com o que divergir. Ela é
   * espelho, nunca fonte: quem a grava é `syncGlobalAccountKey()`.
   */
  get isGlobalAccount(): boolean {
    const user = this.getUser();

    if (user) {
      return user.globalActivated === true;
    }

    return localStorage.getItem(GLOBAL_ACCOUNT) !== null;
  }

  userChanged: EventEmitter<User> = new EventEmitter();

  /**
   * A sessão foi gravada (`saveData`: login, segundo fator, `?userData=`) ou acabou
   * (`removeData`) sem reload da página.
   *
   * Separado do `userChanged` de propósito: as telas abertas refazem fetch no `userChanged`, e
   * no fim da sessão isso viraria requisição sem token. Quem precisa de estado da sessão em
   * serviço `root` — o realm do `AccountVersionService` — escuta este.
   */
  sessionChanged: EventEmitter<void> = new EventEmitter();

  constructor(
    private http: HttpClient
  ) { }

  /**
   * CU-86ak84mj6 — o login pode terminar em sessão OU em desafio de segundo
   * fator. Só grava dados locais quando veio token; desafio não é sessão.
   */
  login(user: User): Observable<Token | TwoFactorChallenge> {
    return this.http.post<Token | TwoFactorChallenge>(`${environment.apiUrl}/login`, user)
      .pipe(tap(response => {
        if (!isTwoFactorChallenge(response)) {
          this.saveData(response as Token);
        }
      }));
  }

  /** Segundo passo do login de quem já tem autenticador configurado. */
  verifyTwoFactor(twoFactorToken: string, code: string, recoveryCode?: string): Observable<Token> {
    const payload: any = { two_factor_token: twoFactorToken };

    if (recoveryCode) {
      payload.recovery_code = recoveryCode;
    } else {
      payload.code = code;
    }

    return this.http.post<Token>(`${environment.apiUrl}/login/two-factor`, payload)
      .pipe(tap(token => this.saveData(token)));
  }

  // --- Segundo fator pela tela de perfil (CU-86ak84mj6) ---------------------
  //
  // A senha é exigida no setup e na regeração dos códigos: uma sessão roubada
  // não pode mexer no segundo fator sozinha. Desativar não existe por aqui —
  // é justamente o que uma sessão roubada tentaria primeiro.

  twoFactorStatus(): Observable<TwoFactorStatus> {
    return this.http.get<TwoFactorStatus>(`${environment.apiUrl}/two-factor`);
  }

  /** "Não exibir mais" o aviso do dashboard. Dispensa o lembrete, não a exigência. */
  dismissTwoFactorNotice(): Observable<{ notice_dismissed: boolean }> {
    return this.http.post<{ notice_dismissed: boolean }>(`${environment.apiUrl}/two-factor/notice/dismiss`, {});
  }

  startTwoFactorEnrollment(password: string): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>(`${environment.apiUrl}/two-factor/setup`, { password });
  }

  confirmTwoFactorEnrollment(code: string): Observable<{ recovery_codes: string[] }> {
    return this.http.post<{ recovery_codes: string[] }>(`${environment.apiUrl}/two-factor/confirm`, { code });
  }

  regenerateRecoveryCodes(password: string): Observable<{ recovery_codes: string[] }> {
    return this.http.post<{ recovery_codes: string[] }>(
      `${environment.apiUrl}/two-factor/recovery-codes`,
      { password }
    );
  }

  logout(): void {
    // O refresh vai junto para o backend derrubar a família inteira da sessão;
    // sem ele, só o access token seria invalidado.
    this.http
      .post(`${environment.apiUrl}/logout`, { refresh_token: this.getRefreshToken() })
      .subscribe(() => { });

    this.removeData();
  }

  getRefreshToken(): string {
    const authData = this.getSavedData();
    return authData ? authData.refresh_token : null;
  }

  /**
   * Renova o access token pelo refresh. Grava o par novo — o refresh é rotativo,
   * então o anterior deixa de valer no mesmo instante.
   */
  refreshToken(): Observable<Token> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('sem refresh token'));
    }

    return this.http
      .post<Token>(`${environment.apiUrl}/refresh-token`, { refresh_token: refreshToken })
      .pipe(
        tap(token => {
          const authData = this.getSavedData();

          // A resposta do refresh não repete o usuário: preserva o que já está
          // salvo e troca apenas o par de tokens.
          this.saveData({ ...authData, ...token });
        }),
        catchError(error => {
          this.removeData();
          return throwError(() => error);
        })
      );
  }

  checkTokenExpired(): boolean {

    if (false === this.isLogged()) {
      return true;
    }

    const data = this.getSavedData();

    if (Date.now() > data.expiration * 1000) {
      // CU-86ak84mj6 — com refresh disponível, access token vencido não é mais
      // motivo de logout: o RefreshInterceptor renova na primeira requisição.
      // Sem refresh (sessão criada antes desta versão) segue o comportamento
      // antigo, senão o usuário ficaria preso numa sessão que não renova.
      if (this.getRefreshToken()) {
        return false;
      }

      this.logout();
      return true;
    }

    return false;
  }

  removeData(): void {
    // As marcas de "já vi" da etiqueta Novo do menu sobrevivem ao logout (CU-17tm466mg60).
    keepNewBadgeMarks(() => localStorage.clear());
    this.sessionChanged.emit();
  }

  /**
   * Grava a sessão e avisa (`sessionChanged`). Toda sessão nova passa por aqui — login, segundo
   * fator e o "acessar como cliente" do `AppComponent.readQueryString` (`?userData=`) —, e o
   * aviso é o que faz o realm do `AccountVersionService` acompanhar sem reload. Nas regravações
   * da mesma sessão (refresh de token, troca de senha) o `refresh()` de lá não publica nada,
   * porque o realm não mudou.
   */
  saveData(authData: Token): void {
    this.enforceRealmAccess(authData.user);
    localStorage.setItem(USER_KEY, JSON.stringify(authData));
    this.syncGlobalAccountKey();
    this.sessionChanged.emit();
  }

  validateVerifyToken(userId: string, token: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/account-confirmation`, { userId, token })
    .pipe(tap(() => {
      const userData = this.getSavedData();
      userData.user.sms_token_validated = true;
      this.saveData(userData);
    }));
  }

  resendSmsToken(isAutomatic): Observable<ResendTokenResponse> {
    return this.http.post<ResendTokenResponse>(`${environment.apiUrl}/resend-token${isAutomatic ? '?automatic=true' : ''}`, {})
  }

  changePassword(password: string, newPassword: string): Observable<any> {
    return this.http
      .post(`${environment.apiUrl}/change-password`, { password, new_password: newPassword })
      .pipe(tap(() => {
        const userData = this.getSavedData();
        userData.user.temporary = false;
        this.saveData(userData);
      }));
  }

  getSavedData(): any {
    const savedData = localStorage.getItem(USER_KEY);
    if (savedData) {
      return JSON.parse(savedData);
    }
  }

  /**
   * Quem está logado quando é MEMBRO da conta (Multiusuários, CU-17tm466h1cm).
   *
   * `getUser()` continua devolvendo a CONTA (o dono): plano, créditos, flags e
   * a saudação do topo do header são dela. Daqui saem o selo "Membro" e o card
   * de identidade do dropdown (nome e e-mail de quem logou). `null` para o
   * dono e para conta sem membros.
   */
  get currentMember(): AccountMemberSession | null {
    return this.getSavedData()?.member ?? null;
  }

  /** Só usabilidade: quem barra o membro de verdade é o backend (`owner_only`). */
  get isAccountOwner(): boolean {
    return this.currentMember === null;
  }

  getUser(force_original: boolean = false): User {
    const simulatedUser = this.getSimulatedUser();
    if (simulatedUser && !force_original) {
      return this.enforceRealmAccess(simulatedUser);
    }

    const authData = this.getSavedData();
    if (authData) {
      return this.enforceRealmAccess(authData.user);
    }
  }

  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/users/${id}`);
  }

  getLoggedInUser(): User {
    let authData = null;
    const savedData = localStorage.getItem(USER_KEY);
    if (savedData) {
      authData = JSON.parse(savedData);
    }
    if (authData) {
      return authData.user;
    }
  }

  /**
   * Troca o usuário da sessão EM CURSO — é esse o recorte do método, e é dele
   * que sai a preservação do realm (ver `preserveRealm`): o registro
   * sobrescrito é o mesmo de antes, então o que o payload não disser continua
   * valendo.
   *
   * QUAL registro é esse sai do ID que chega, não de quem chamou
   * (CU-86akku3z4). Durante uma simulação o usuário da sessão em curso é o
   * cliente simulado — é ele que `getUser()` devolve e dele que sai todo
   * `&global=` —, mas este método gravava sempre em `authData.user`, que é o
   * registro do ADMIN. O polling de notificação do header lia o efetivo,
   * buscava esse cliente no backend e mandava o payload para cá: o
   * `SMSFUNNEL::AUTH` do admin passava a guardar os dados do cliente,
   * `isSimulating` (que compara os dois ids) virava `false` — e a partir dali
   * `updateUser` escrevia no lugar errado — e a emissão saía sem `simulated`,
   * derrubando da tela o botão de voltar ao usuário logado com a simulação
   * ainda ativa. De 30 em 30 segundos, sem ninguém clicar em nada.
   *
   * A alternativa era o desvio `if (getSimulatedUser())` no call site, que é o
   * que os outros chamadores fazem. Ela é a forma que JÁ falhou aqui: acerta
   * quem lembra e deixa o próximo esquecer, em silêncio — o estrago aparece no
   * disco, não na tela de quem escreveu a linha. E o call site não tem como
   * decidir direito: quem sabe se o payload descreve o usuário simulado é quem
   * tem os DOIS registros na mão, que é este serviço. O call site só tem o
   * payload.
   *
   * A escrita no simulado DELEGA a `setSimulatedUser` em vez de gravar a chave
   * por fora: ela continua sendo a porta de entrada da simulação e a única
   * escritora daquele registro — dois escritores do mesmo fato foi a causa da
   * CU-86akktdka. A preservação do realm roda antes, contra o registro de
   * DESTINO: é ele que está sendo sobrescrito. Preservar contra o admin um
   * payload do cliente simulado seria trazer o realm de outra conta, que é o
   * furo que a CU-86akktwth deixou `saveData` de fora para não abrir.
   *
   * A ORDEM continua sendo contrato, não estilo: grava primeiro, emite depois.
   * Quem reage a `userChanged` refaz fetch na hora, e o `UserInterceptor` monta
   * a query string lendo o usuário GRAVADO — emitir antes de gravar faria a
   * tela refazer o fetch falando do estado anterior. Vale nos dois caminhos:
   * `setSimulatedUser` grava antes de emitir pelo mesmo motivo.
   *
   * Emitir também é trabalho deste método: quem chama não repete o
   * `userChanged.emit` depois (CU-86akktwth).
   */
  setUser(user: User): void {
    const simulatedUser = this.getSimulatedUser();

    if (user && simulatedUser && user.id === simulatedUser.id) {
      this.preserveRealm(user, simulatedUser);
      this.setSimulatedUser(user);
      return;
    }

    const authData = this.getSavedData();
    if (authData) {
      this.preserveRealm(user, authData.user);
      authData.user = user;
      this.saveData(authData);
      this.userChanged.emit(user);
    }
  }

  getToken(): string {
    const authData = this.getSavedData();
    if (authData) {
      return authData.access_token;
    }
  }

  isLogged(): boolean {
    const user = this.getUser();
    return user ? true : false;
  }

  /**
   * Entra na simulação (com usuário) ou volta para o usuário real (sem).
   *
   * É também a ÚNICA escritora do `SMSFUNNEL::SIMULATED_USER`: desde a
   * CU-86akku3z4 o `setUser` delega para cá quando o payload descreve o cliente
   * simulado, em vez de gravar a chave por fora. Porta de entrada e escritora
   * única são a mesma coisa de propósito — dois donos do mesmo registro foi o
   * defeito da CU-86akktdka.
   *
   * Trocar o usuário efetivo é trocar o realm junto, então vale aqui o mesmo
   * contrato de ordem de `setUser`: grava primeiro, emite depois. Quem reage a
   * `userChanged` refaz fetch na hora, e o `UserInterceptor` monta `&global=`
   * lendo o usuário gravado nesse instante.
   */
  setSimulatedUser(user?: User) {
    if (user) {
      user.simulated = true;
      // Mesmo invariante do `saveData`, e antes da gravação: o realm do cliente
      // simulado vale apenas até onde o acesso dele vai.
      this.enforceRealmAccess(user);
      localStorage.setItem(SIMULATED_USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(SIMULATED_USER_KEY);
    }
    user = this.getUser();
    this.syncGlobalAccountKey();
    this.userChanged.emit(user);
  }

  getSimulatedUser(): User {
    const localUser = localStorage.getItem(SIMULATED_USER_KEY);
    if (localUser) {
      return JSON.parse(localUser) as User;
    }

    return null;
  }

  updateUser(user: User) {
    if (this.isSimulating) {
      this.setSimulatedUser(user);
    } else {
      this.setUser(user);
    }
  }

  /**
   * Alterna o realm da conta (nacional ↔ internacional).
   *
   * Tudo que o realm decide é gravado ANTES de qualquer emissão: o
   * `globalActivated` do usuário efetivo e o espelho do dispositivo, ambos
   * dentro de `updateUser`, que só então emite `userChanged` (ver `setUser` e
   * `setSimulatedUser`). Inverter a ordem é o que quebra a reatividade de
   * verdade — a tela refaria o fetch ainda falando do realm anterior, porque é
   * isso que o `UserInterceptor` encontraria gravado na hora de montar
   * `&global=`.
   *
   * O flag vai no usuário efetivo, que é quem responde por `isGlobalAccount`:
   * durante uma simulação o realm trocado é o do cliente simulado, e é só dele.
   *
   * Conta sem `intl_enabled` não chega ao internacional por aqui: a gravação
   * passa por `enforceRealmAccess` e o toggle vira no-op. O switch que chama
   * este método já não aparece para ela (`canSwitchVersion` do header), mas a
   * garantia é do invariante, não da tela.
   */
  toggleGlobalAccount(): void {
    const user = this.getUser();

    user.globalActivated = !this.isGlobalAccount;

    this.updateUser(user);
  }

  /**
   * Mantém o realm quando o payload que chega não fala dele (CU-86akktwth).
   *
   * `globalActivated` é campo SÓ DO FRONTEND: nenhuma rota do backend o devolve.
   * Então todo `setUser(payloadDoBackend)` chega sem ele — o refresh do usuário
   * pelo polling de notificação do header, o retorno do `users.save` do perfil,
   * da chave pix, da recarga —, e gravar o payload cru zerava o realm: quem
   * estivesse no internacional era jogado para o nacional NO MEIO DA SESSÃO, sem
   * nada na tela avisando. Com carteira em duas moedas isso é leitura de
   * dinheiro errada, e no header o gatilho era um polling de 30 em 30 segundos.
   *
   * A proteção é aqui, e não no call site, porque o call site fecha UM caminho e
   * deixa o próximo `setUser` com o mesmo buraco — e porque é a definição deste
   * método que torna a preservação correta: trocar o usuário da sessão em curso
   * é sobrescrever o MESMO registro, e o realm dele não vira notícia velha só
   * porque o backend não repetiu o campo.
   *
   * `saveData` fica de fora de propósito: por lá também entra o LOGIN, e
   * preservar ali traria o realm da sessão ANTERIOR — outra conta, exatamente o
   * relogar-sem-logout que a CU-86akktdka listou como furo. `setSimulatedUser`
   * também: entrar numa simulação é trocar de conta, e o realm do simulado é só
   * dele.
   *
   * O que separa um caso do outro é o ID, não o método, e quem confere é o
   * `setUser` (CU-86akku3z4): ele chama esta função contra o registro de
   * DESTINO — o simulado quando o payload é do cliente simulado, o da sessão
   * quando não é. Entrar numa simulação continua sem preservar nada porque ali
   * os ids não batem; refrescar o cliente que já está sendo simulado preserva,
   * porque é o mesmo registro sendo sobrescrito. Quem chama `setSimulatedUser`
   * direto passa por fora desta regra.
   *
   * O gatilho é o campo AUSENTE, nunca o campo falso. `toggleGlobalAccount`
   * manda `false` de propósito ao voltar para o nacional; preservar em cima de
   * um `false` explícito prenderia a conta no internacional — o oposto do que
   * esta função existe para evitar.
   *
   * Isto NÃO concorre com `enforceRealmAccess`: são regras sobre campos
   * diferentes e rodam em sequência. Esta preenche o `globalActivated` que o
   * payload não trouxe; aquela roda depois, dentro de `saveData`, e decide se
   * esse realm é permitido pelo `intl_enabled`. O invariante segue com a última
   * palavra — preservar nunca ressuscita um realm que ele derrubaria — e a
   * ordem inversa mataria esta função, porque o invariante normaliza o campo
   * ausente para `false` e não sobraria ausência nenhuma para preservar.
   */
  private preserveRealm(incoming: User, persisted: User): void {
    if (incoming && incoming.globalActivated === undefined) {
      incoming.globalActivated = persisted?.globalActivated === true;
    }
  }

  /**
   * Invariante do realm (CU-86akktm54): internacional exige acesso ao
   * internacional.
   *
   * O realm mora no `globalActivated` do usuário efetivo; o acesso mora no
   * `intl_enabled`, que é do backend. Enquanto os dois podiam discordar, a conta
   * que estivesse no internacional e perdesse o acesso ficava PRESA lá: o painel
   * seguia internacional (o realm é dela) e o único caminho de volta — o item do
   * dropdown do header — some junto com `intl_enabled`, que é o que o libera.
   * Mesmo efeito para o cliente simulado cujo payload trouxesse
   * `globalActivated: true` sem `intl_enabled`. Com carteira em duas moedas isso
   * é leitura de dinheiro errada, não tela feia.
   *
   * A outra saída possível era manter o switch visível enquanto `globalActivated`
   * fosse `true`. Ela dá escape e deixa o estado inconsistente continuar
   * alcançável — e cria uma SEGUNDA definição de "pode ser internacional",
   * concorrendo com `intl_enabled`, que é o inverso do que a CU-86akktdka fez ao
   * reduzir o realm a um valor só. Derrubar o realm não conserta o estado preso:
   * tira a possibilidade dele existir, porque sem acesso não há internacional de
   * onde sair. Não troque isto por um escape no header.
   *
   * Vale na LEITURA e na ESCRITA, de propósito. Na leitura porque `getUser()` é o
   * funil por onde o realm sai para o `UserInterceptor`, o `MenuService` e o
   * `isGlobalAccount` — e porque sessão gravada pela versão anterior a esta
   * correção já está em disco com o par inconsistente. Na escrita para o disco
   * não guardar um valor que toda leitura desmente: dois registros do mesmo fato
   * foi exatamente a causa da CU-86akktdka.
   *
   * O `false` para `globalActivated` ausente continua aqui: a listagem de
   * usuários não devolve o campo, e sem ele o interceptor mandaria
   * `global=undefined` para o backend.
   */
  private enforceRealmAccess(user: User): User {
    if (user) {
      user.globalActivated = user.globalActivated === true && user.intl_enabled === true;
    }

    return user;
  }

  /**
   * Espelha no dispositivo o realm do usuário efetivo.
   *
   * Chamado de todo ponto que troca esse usuário — e sempre ANTES da emissão de
   * `userChanged`, porque o contrato de ordem vale para o espelho também. É o
   * que impede a chave de sobreviver a uma simulação carregando o realm de
   * outra conta.
   */
  private syncGlobalAccountKey(): void {
    if (this.isGlobalAccount) {
      localStorage.setItem(GLOBAL_ACCOUNT, 'true');
    } else {
      localStorage.removeItem(GLOBAL_ACCOUNT);
    }
  }
}
